import React from "react";
import { useTranslation } from "react-i18next";

/**
 * 英文版用户协议内容
 */
export const UserAgreementContentEn: React.FC = () => {
  return (
    <>
      <p>Last Updated: January 9, 2026</p>
      <p>
        These Terms of Service (&ldquo;Terms&rdquo;) govern all use of the
        GenOptima AI Insights Platform and related services
        (&ldquo;Service&rdquo;) provided by GenOptima / 智推时代
        (&ldquo;GenOptima,&rdquo; &ldquo;Provider,&rdquo; &ldquo;we,&rdquo;
        &ldquo;us,&rdquo; or &ldquo;our&rdquo;). The Service is intended
        exclusively for business customers. Use by consumers is excluded. By
        registering for, accessing, or using the Service, you
        (&ldquo;Customer,&rdquo; &ldquo;you&rdquo;) agree to be bound by these
        Terms. If you do not agree, you must not access or use the Service.
      </p>

      <h3>1. Scope and Parties</h3>
      <p>
        1.1 These Terms govern all use of the Service by customers who qualify
        as business entities. The Customer may use the Service solely within the
        scope of its own business operations and by its own personnel.
      </p>
      <p>
        1.2 GenOptima is a company incorporated under the laws of Singapore (or
        as applicable). The Provider and the Customer agree that only these
        Terms shall govern the contract.
      </p>
      <p>
        1.3 Any deviating or supplemental terms of the Customer will not become
        part of the contract unless we have expressly agreed to them in writing.
      </p>

      <h3>2. Services Provided by GenOptima</h3>
      <p>
        2.1 Core Service: We provide a Software as a Service
        (&ldquo;SaaS&rdquo;) that offers AI-powered analytics and insights to
        help organizations understand brand, product, content, or entity
        representation within large language models (&ldquo;LLMs&rdquo;) and
        AI-driven systems.
      </p>
      <h4>What the Service Does:</h4>
      <ul>
        <li>AI visibility monitoring and analysis;</li>
        <li>Prompt-based observation and response tracking;</li>
        <li>Aggregation of AI-generated outputs;</li>
        <li>Comparative or historical analytics;</li>
        <li>Reporting, dashboards, and insight summaries.</li>
      </ul>
      <h4>What the Service Does Not Do:</h4>
      <ul>
        <li>Assess or analyze data beyond described functionalities;</li>
        <li>Promote or influence brand visibility;</li>
        <li>Provide strategic, legal, marketing, or business advice;</li>
        <li>Guarantee suitability, accuracy, or effectiveness of prompts.</li>
      </ul>
      <p>
        2.2 Provision of Software: GenOptima shall grant the Customer access to
        and use of the then-current version of the Software via the Internet.
      </p>
      <p>
        2.3 Service Modifications: GenOptima may make reasonable changes to the
        Service (e.g., to improve performance or comply with laws) provided such
        changes do not eliminate core features.
      </p>
      <p>
        2.4 Third-Party Components: The Service may rely on third-party
        platforms, APIs, or AI models. Availability of external services is
        outside our control.
      </p>
      <p>
        2.5 No Guaranteed Results: GenOptima does not guarantee specific
        outputs. The Customer may provide input (e.g., prompts, URLs)
        (&ldquo;Input&rdquo;) and receive output (&ldquo;Output&rdquo;). Quality
        depends on Input; we do not warrant Outputs meet expectations.
      </p>

      <h3>3. Service Levels; Maintenance and Interruptions</h3>
      <p>
        3.1 Service Levels: We aim for 99% monthly availability at the delivery
        point. Availability excludes scheduled maintenance, force majeure, and
        Customer-caused interruptions.
      </p>
      <p>
        3.2 Maintenance: We may maintain the software and inform you of
        restrictions.
      </p>
      <p>
        3.3 Other Interruptions: We do not control LLMs and do not warrant
        error-free operation but will use reasonable efforts to address issues.
      </p>

      <h3>4. Use Requirements</h3>
      <p>
        4.1 Responsibilities: Use the Service only for legitimate business
        purposes and in compliance with laws. Keep credentials confidential. You
        are responsible for Content and warrant necessary rights.
      </p>
      <p>
        4.2 Prohibited Uses: Do not reverse-engineer, circumvent limitations, or
        misuse the Service. In breach, we may suspend access after notice and/or
        terminate.
      </p>

      <h3>5. Intellectual Property and Data</h3>
      <p>
        5.1 Service IP: All rights in the Service remain with GenOptima. We
        grant a limited, non-exclusive, non-transferable right to use during the
        term for internal business.
      </p>
      <p>
        5.2 Customer Data: You retain rights to data provided (&ldquo;Customer
        Data&rdquo;). You grant us a license to process only as necessary for
        the Service. We comply with data protection laws and our Privacy Policy.
        Upon termination, we delete or return data except as legally required.
      </p>

      <h3>6. Fees, Payment Terms, and Set-off</h3>
      <p>6.1 Fees: As agreed in the Order. Prices net of GST/VAT.</p>
      <p>
        6.2 Invoicing, Payment, and Suspension: Invoiced per cycle; due within
        14 days. Late payments accrue interest at statutory rates. We may
        suspend for non-payment after notice.
      </p>
      <p>
        6.3 No Set-off Except for Uncontested Claims: Set-off only for
        undisputed or adjudicated claims.
      </p>

      <h3>7. Trial Access</h3>
      <p>
        7.1 Trial Access: We may offer free trial for evaluation (&ldquo;Trial
        Period&rdquo;).
      </p>
      <p>7.2 Scope: Limited features; for evaluation only.</p>
      <p>
        7.3 Termination: We may terminate trial anytime. Post-trial requires
        subscription.
      </p>

      <h3>8. Warranty</h3>
      <p>8.1 Applicable Provisions: Warranties as per Singapore law.</p>
      <p>8.2 Notification of Defects: Notify us promptly of defects.</p>
      <p>8.3 Exclusion: No warranty for insignificant impairments.</p>

      <h3>9. Limitation of Liability</h3>
      <p>
        9.1 Unlimited Liability: For intent, gross negligence, injury to
        life/body/health, or under product liability laws.
      </p>
      <p>
        9.2 Slight Negligence: Limited to breach of essential obligations,
        capped at foreseeable typical damages.
      </p>
      <p>9.3 Exclusion: No liability for non-essential slight negligence.</p>
      <p>9.4 Further Limitations: Applies to our agents.</p>
      <p>
        9.5 Indemnification: You indemnify us for claims from your misuse or
        breach.
      </p>

      <h3>10. Term and Termination</h3>
      <p>
        10.1 Contract Term: Begins on acceptance; as per Order or indefinite.
      </p>
      <p>
        10.2 Ordinary Termination: Monthly: end of cycle; Annual: 30 days&apos;
        notice; Indefinite: 30 days to month-end.
      </p>
      <p>10.3 Termination for Cause: Immediate for material breach.</p>
      <p>10.4 Effects: Access ceases; data handled per Section 5.</p>

      <h3>11. Governing Law and Dispute Resolution</h3>
      <p>
        These Terms are governed by the laws of Singapore. Disputes shall be
        submitted to arbitration in Singapore under SIAC rules, or exclusive
        jurisdiction of Singapore courts.
      </p>

      <h3>12. Miscellaneous</h3>
      <p>12.1 Assignment: Not without consent, except mergers.</p>
      <p>12.2 Force Majeure: Excused for uncontrollable events.</p>
      <p>12.3 Severability: Invalid provisions severed.</p>
      <p>
        12.4 Contact:{" "}
        <a href="mailto:info@gen-optima.com">info@gen-optima.com</a>
      </p>
    </>
  );
};

/**
 * 中文版用户协议内容
 */
export const UserAgreementContentZh: React.FC = () => {
  const { t } = useTranslation();

  return (
    <>
      <p>
        欢迎使用智推时代AI洞察平台。本协议旨在明确您与我们之间的权利和义务关系，确保您能够充分了解并合规使用我们的服务。
      </p>

      <h3>{t("common.modals.term_1_service")}</h3>
      <p>
        通过注册、登录或使用智推时代AI洞察平台的任何功能，即表示您已阅读、理解并同意接受本协议的全部条款。若您不同意本协议的任何内容，请立即停止使用我们的服务。
      </p>

      <h3>{t("common.modals.term_2_account")}</h3>
      <ul>
        <li>
          账户创建：您需要提供真实、准确、完整的注册信息完成账户创建，并确保及时更新相关信息。
        </li>
        <li>
          账户安全：您对账户密码和验证信息负有保密责任，所有通过您账户进行的操作均视为您本人的行为。
        </li>
        <li>
          使用规范：不得将账户转让、出租或出借给他人使用，不得利用账户从事任何违法违规活动。
        </li>
      </ul>

      <h3>{t("common.modals.term_3_usage")}</h3>
      <ul>
        <li>
          合法使用：您承诺仅将服务用于合法的商业目的，不得用于任何违反法律法规或侵犯他人权益的活动。
        </li>
        <li>
          内容规范：不得上传、发布或传播任何违法、侵权、虚假、骚扰性或其他不当内容。
        </li>
        <li>
          技术限制：不得尝试破解、反向工程或干扰服务的正常运行，不得利用技术漏洞获取未授权信息。
        </li>
      </ul>

      <h3>{t("common.modals.term_4_ip")}</h3>
      <p>
        智推时代AI洞察平台的所有内容，包括但不限于软件、代码、界面设计、图表、数据、商标等，均受相关知识产权法律保护。未经我们明确书面授权，您不得：
      </p>
      <ul>
        <li>复制、修改、分发或公开展示平台的任何部分</li>
        <li>创建基于平台服务的衍生作品</li>
        <li>未经授权使用我们的商标、标识或其他专有信息</li>
      </ul>

      <h3>{t("common.modals.term_5_security_privacy")}</h3>
      <p>我们高度重视您的数据安全，采取多层次技术和管理措施保护您的信息：</p>
      <ul>
        <li>数据传输采用加密技术，防止信息在传输过程中被窃取</li>
        <li>数据存储实施严格的访问控制和权限管理</li>
        <li>定期进行安全审计和漏洞修复，确保系统安全性</li>
      </ul>

      <h3>{t("common.modals.term_6_changes")}</h3>
      <p>为持续优化用户体验，我们保留根据业务发展需要调整服务内容的权利：</p>
      <ul>
        <li>服务优化：可能新增、修改或暂停部分功能，重要变更将提前通知用户</li>
        <li>
          服务终止：如因不可抗力或业务调整需要终止服务，将提前公告并妥善处理用户数据
        </li>
        <li>
          违规处理：对于严重违反本协议的用户，我们有权暂停或终止其账户使用权
        </li>
      </ul>

      <h3>{t("common.modals.term_7_liability")}</h3>
      <p>在法律允许的最大范围内，我们对以下情况不承担责任：</p>
      <ul>
        <li>因不可抗力、第三方服务故障或网络问题导致的服务中断或数据丢失</li>
        <li>用户违反本协议使用服务所造成的任何直接或间接损失</li>
        <li>因用户提供不实信息或操作失误导致的任何问题</li>
      </ul>

      <h3>{t("common.modals.term_8_contact")}</h3>
      <p>
        如您对本协议有任何疑问、建议或需要投诉，欢迎通过邮箱{" "}
        <a href="mailto:cmz@gen-optima.com">cmz@gen-optima.com</a>{" "}
        与我们取得联系。我们将在收到信息后及时处理并回复。
      </p>

      <p className="last-updated">
        {t("common.modals.last_updated_agreement")}
      </p>
    </>
  );
};
